<template>
    <div class="ranking-departments-report">
        <div class="card shadow-sm border-0">

            <HeaderFilter
                title="Ranking de Centro de Custos"
                @apply-filters="loadData"
            />

            <div class="card-body">
                <!-- Loading -->
                <div
                    v-if="loadingData"
                    class="text-center py-4"
                >
                    <i class="fas fa-spinner fa-spin fa-2x text-info"></i>
                    <p class="mt-2">Carregando ranking...</p>
                </div>

                <!-- Conteúdo Principal -->
                <div v-else>
                    <!-- Top 3 Centro de Custos -->
                    <div
                        class="top-departments mb-4"
                        v-if="tableData.length > 0"
                    >
                        <div class="row">
                            <!-- 2º Lugar -->
                            <div
                                class="col-md-4 order-md-1"
                                v-if="tableData[1]"
                            >
                                <div class="podium-card second-place">
                                    <div class="medal-icon">
                                        <i class="fas fa-medal"></i>
                                        <span class="position">2º</span>
                                    </div>
                                    <div class="department-info">
                                        <div class="department-name">{{ truncateName(tableData[1].description) }}</div>
                                        <div class="department-count">{{ tableData[1].total }} reservas</div>
                                    </div>
                                </div>
                            </div>

                            <!-- 1º Lugar -->
                            <div
                                class="col-md-4 order-md-2"
                                v-if="tableData[0]"
                            >
                                <div class="podium-card first-place">
                                    <div class="medal-icon">
                                        <i class="fas fa-crown"></i>
                                        <span class="position">1º</span>
                                    </div>
                                    <div class="department-info">
                                        <div class="department-name">{{ truncateName(tableData[0].description) }}</div>
                                        <div class="department-count">{{ tableData[0].total }} reservas</div>
                                    </div>
                                    <div class="winner-badge">
                                        <i class="fas fa-trophy"></i>
                                        LÍDER
                                    </div>
                                </div>
                            </div>

                            <!-- 3º Lugar -->
                            <div
                                class="col-md-4 order-md-3"
                                v-if="tableData[2]"
                            >
                                <div class="podium-card third-place">
                                    <div class="medal-icon">
                                        <i class="fas fa-medal"></i>
                                        <span class="position">3º</span>
                                    </div>
                                    <div class="department-info">
                                        <div class="department-name">{{ truncateName(tableData[2].description) }}</div>
                                        <div class="department-count">{{ tableData[2].total }} reservas</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Estatísticas Rápidas -->
                    <div
                        class="quick-stats mb-4"
                        v-if="tableData.length > 0"
                    >
                        <div class="row">
                            <div class="col-md-3">
                                <div class="stat-item">
                                    <div class="stat-value">{{ tableData.length }}</div>
                                    <div class="stat-label">Total de Centro de Custos</div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="stat-item">
                                    <div class="stat-value">{{ totalReservations }}</div>
                                    <div class="stat-label">Total de Reservas</div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="stat-item">
                                    <div class="stat-value">{{ avgReservationsPerDept }}</div>
                                    <div class="stat-label">Média por Depto</div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="stat-item">
                                    <div class="stat-value">{{ concentrationIndex }}%</div>
                                    <div class="stat-label">Concentração Top 3</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Gráfico -->
                    <div
                        class="chart-container mb-4"
                        v-if="tableData.length > 0"
                    >
                        <GChart
                            v-if="renderChart"
                            type="ColumnChart"
                            :data="chartData"
                            :options="chartOptions"
                            style="width: 100%; height: 350px;"
                        />
                    </div>

                    <!-- Tabela Completa -->
                    <div class="ranking-table">
                        <div class="table-responsive">
                            <table class="table table-hover">
                                <thead class="bg-light">
                                <tr>
                                    <th width="10%">Posição</th>
                                    <th width="50%">Centro de Custo</th>
                                    <th
                                        width="20%"
                                        class="text-center"
                                    >Reservas
                                    </th>
                                    <th
                                        width="20%"
                                        class="text-center"
                                    >Participação
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr
                                    v-for="(department, index) in tableData"
                                    :key="index"
                                >
                                    <td>
                                        <div
                                            class="position-badge"
                                            :class="getPositionClass(index + 1)"
                                        >
                                            <span v-if="index < 3">
                                                <i :class="getPositionIcon(index + 1)"></i>
                                            </span>
                                            {{ index + 1 }}º
                                        </div>
                                    </td>
                                    <td>
                                        <div class="department-info-row">
                                            <div class="department-icon">
                                                <i class="fas fa-building"></i>
                                            </div>
                                            <div class="department-details">
                                                <div class="department-name">{{ department.description }}</div>
                                                <div class="department-meta text-muted">
                                                    {{ getParticipationText(department.total) }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="text-center">
                                        <span class="badge badge-info">{{ department.total }}</span>
                                    </td>
                                    <td class="text-center">
                                        <div class="participation-bar">
                                            <div class="participation-progress">
                                                <div
                                                    class="progress-fill"
                                                    :style="`width: ${getParticipationPercentage(department.total)}%`"
                                                ></div>
                                            </div>
                                            <span class="participation-text">
                                                {{ getParticipationPercentage(department.total) }}%
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-if="tableData.length === 0">
                                    <td
                                        colspan="4"
                                        class="text-center text-muted py-4"
                                    >
                                        Nenhum departamento encontrado para o período selecionado
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Análise de Distribuição -->
                    <div
                        class="distribution-analysis mt-4"
                        v-if="tableData.length > 0"
                    >
                        <div class="row">
                            <div class="col-md-6">
                                <div class="analysis-card">
                                    <h6 class="analysis-title">
                                        <i class="fas fa-chart-pie mr-2"></i>
                                        Análise de Distribuição
                                    </h6>
                                    <div class="analysis-content">
                                        <div class="analysis-item">
                                            <span class="analysis-label">Top 3 Centro de Custos:</span>
                                            <span class="analysis-value">{{ top3Percentage }}%</span>
                                        </div>
                                        <div class="analysis-item">
                                            <span class="analysis-label">Centro de Custos com 1-5 reservas:</span>
                                            <span class="analysis-value">{{ lowActivityDepts }}</span>
                                        </div>
                                        <div class="analysis-item">
                                            <span class="analysis-label">Centro de Custos com 6+ reservas:</span>
                                            <span class="analysis-value">{{ highActivityDepts }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="analysis-card">
                                    <h6 class="analysis-title">
                                        <i class="fas fa-trophy mr-2"></i>
                                        Destaques do Período
                                    </h6>
                                    <div class="analysis-content">
                                        <div class="analysis-item">
                                            <span class="analysis-label">Centro de Custo Líder:</span>
                                            <span class="analysis-value text-success">{{ leaderDepartment }}</span>
                                        </div>
                                        <div class="analysis-item">
                                            <span class="analysis-label">Diferença para 2º:</span>
                                            <span class="analysis-value text-info">{{ leaderGap }} reservas</span>
                                        </div>
                                        <div class="analysis-item">
                                            <span class="analysis-label">Maior Crescimento:</span>
                                            <span class="analysis-value text-warning">{{ growthDepartment }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {GChart} from "vue-google-charts";
import vSelect from "vue-select";
import {getMessage} from "../../composables/message";
import HeaderFilter from "./HeaderFilter.vue";

export default {
    name: "RankingDepartmentsReport", // nome do componente

    components: {
        HeaderFilter,
        GChart, // componente de gráfico do Google Charts
        vSelect, // componente de select
    },

    props: {
        globalFilters: {
            type: Object,
            default: () => ({}) // filtros globais passados pelo componente pai
        },
        loading: {
            type: Boolean,
            default: false // estado de carregamento externo
        }
    },

    data() {
        return {
            loadingData: false,
            renderChart: false,

            // chartOptions - opções de configuração do gráfico
            chartOptions: {
                chart: {
                    title: "Ranking de Centro de Custos",
                    subtitle: "Centro de Custos com maior número de reservas"
                },
                colors: ["#f49e00", "#1b9e77"],
                hAxis: {
                    title: "Centro de Custos",
                    textStyle: {fontSize: 9}
                },
                vAxis: {
                    title: "Quantidade de Reservas",
                    format: "decimal",
                    minValue: 0
                },
                height: 350,
                legend: {position: "none"},
                bar: {groupWidth: "75%"},
                animation: {
                    duration: 1000,
                    easing: 'out',
                    startup: true
                }
            },

            // tableData - dados para a tabela e gráfico
            tableData: [],
        };
    },

    computed: {
        chartData() {
            if (this.tableData.length === 0) return [["Centro de Custo", "Quantidade"]];

            const chartData = [["Centro de Custo", "Quantidade"]];

            // Pega apenas os top 10 para o gráfico não ficar poluído
            this.tableData.slice(0, 10).forEach(dept => {
                chartData.push([this.truncateName(dept.description, 20), dept.total]);
            });

            return chartData;
        },

        totalReservations() {
            return this.tableData.reduce((sum, dept) => sum + (dept.total || 0), 0);
        },

        avgReservationsPerDept() {
            if (this.tableData.length === 0) return 0;
            return Math.round(this.totalReservations / this.tableData.length);
        },

        concentrationIndex() {
            if (this.tableData.length === 0 || this.totalReservations === 0) return 0;
            const top3Total = this.tableData.slice(0, 3).reduce((sum, dept) => sum + dept.total, 0);
            return Math.round((top3Total / this.totalReservations) * 100);
        },

        top3Percentage() {
            return this.concentrationIndex;
        },

        lowActivityDepts() {
            return this.tableData.filter(dept => dept.total >= 1 && dept.total <= 5).length;
        },

        highActivityDepts() {
            return this.tableData.filter(dept => dept.total >= 6).length;
        },

        leaderDepartment() {
            if (this.tableData.length === 0) return 'N/A';
            return this.truncateName(this.tableData[0].description, 25);
        },

        leaderGap() {
            if (this.tableData.length < 2) return 0;
            return this.tableData[0].total - this.tableData[1].total;
        },

        growthDepartment() {
            // Como não temos dados históricos, retornamos o que tem maior proporção
            if (this.tableData.length === 0) return 'N/A';
            return this.truncateName(this.tableData[0].description, 25);
        },
    },

    watch: {
        // Observa mudanças nos filtros globais / Watch global filters changes
        globalFilters: {
            handler() {
                this.loadData();
            },
            deep: true
        },

        // Observa mudanças no estado de loading externo / Watch external loading state
        loading(newVal) {
            if (!newVal) {
                this.loadData();
            }
        }
    },

    mounted() {
        this.loadData();
    },

    methods: {
        /**
         * Carrega dados do relatório / Load report data
         */
        async loadData(params = null) {
            this.loadingData = true;
            this.renderChart = false;

            try {
                const api = this.$store.state.api + "ranking-cost-centers";

                const response = await this.$http.get(api, {
                    params
                });

                this.tableData = response.data.table || [];

                setTimeout(() => {
                    this.renderChart = true;
                }, 100);

                // Emite evento para o componente pai / Emit event to parent component
                this.$emit('data-updated', 'ranking-departments', response.data);

            } catch (error) {
                console.error('Erro ao carregar ranking de departamentos:', error);
                const message = error.response.data.errors
                    ? getMessage(error.response.data.errors)
                    : error.response.data || 'Erro ao carregar dados';
                this.$message(null, message, "error");
            } finally {
                this.loadingData = false;
            }
        },

        /**
         * Retorna classe CSS para posição / Return CSS class for position
         */
        getPositionClass(position) {
            if (position === 1) return 'first';
            if (position === 2) return 'second';
            if (position === 3) return 'third';
            return 'other';
        },

        /**
         * Retorna ícone para posição / Return icon for position
         */
        getPositionIcon(position) {
            if (position === 1) return 'fas fa-crown';
            if (position === 2) return 'fas fa-medal';
            if (position === 3) return 'fas fa-medal';
            return '';
        },

        /**
         * Retorna texto de participação / Return participation text
         */
        getParticipationText(total) {
            if (total === 1) return '1 reserva';
            return `${total} reservas`;
        },

        /**
         * Calcula percentual de participação / Calculate participation percentage
         */
        getParticipationPercentage(deptTotal) {
            if (this.totalReservations === 0) return 0;
            return Math.round((deptTotal / this.totalReservations) * 100);
        },

        /**
         * Trunca nome para o gráfico / Truncate name for chart
         */
        truncateName(name, maxLength = 30) {
            if (!name) return '';
            if (name.length <= maxLength) return name;
            return name.substring(0, maxLength - 3) + '...';
        },
    },
};
</script>

<style scoped>
/* Estilo principal do relatório */
.ranking-departments-report .card {
    border-radius: 12px;
    overflow: hidden;
}

.bg-gradient-info {
    background: linear-gradient(135deg, #36b9cc 0%, #258391 100%);
}

/* Top 3 departamentos - Pódio */
.top-departments {
    margin-bottom: 2rem;
}

.podium-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    text-align: center;
    position: relative;
    border: 2px solid #e3e6f0;
    transition: all 0.3s ease;
    height: 200px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-bottom: 1rem;
}

.podium-card.first-place {
    border-color: #ffd700;
    background: linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%);
    transform: scale(1.05);
}

.podium-card.second-place {
    border-color: #c0c0c0;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.podium-card.third-place {
    border-color: #cd7f32;
    background: linear-gradient(135deg, #fdf6f0 0%, #f4e5d3 100%);
}

.medal-icon {
    font-size: 2rem;
    margin-bottom: 1rem;
    position: relative;
}

.first-place .medal-icon {
    color: #ffd700;
}

.second-place .medal-icon {
    color: #c0c0c0;
}

.third-place .medal-icon {
    color: #cd7f32;
}

.position {
    position: absolute;
    top: -0.5rem;
    right: -0.5rem;
    background: #FE8D18;
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: bold;
}

.department-name {
    font-weight: 700;
    font-size: 1rem;
    color: #2d3748;
    margin-bottom: 0.5rem;
    line-height: 1.2;
}

.department-count {
    color: #718096;
    font-weight: 600;
}

.winner-badge {
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
    color: #b45309;
    padding: 0.25rem 0.75rem;
    border-radius: 15px;
    font-size: 0.75rem;
    font-weight: 700;
    box-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
}

/* Estatísticas rápidas */
.quick-stats {
    background: #f8f9fc;
    border-radius: 8px;
    padding: 1.5rem;
}

.stat-item {
    text-align: center;
}

.stat-value {
    font-size: 1.75rem;
    font-weight: 800;
    color: #2d3748;
    margin-bottom: 0.5rem;
}

.stat-label {
    font-size: 0.875rem;
    color: #718096;
    text-transform: uppercase;
    font-weight: 600;
}

/* Container do gráfico */
.chart-container {
    background: white;
    border-radius: 8px;
    padding: 1rem;
    border: 1px solid #e3e6f0;
}

/* Tabela de ranking */
.ranking-table {
    background: white;
    border-radius: 8px;
    overflow: hidden;
}

.table {
    margin-bottom: 0;
}

.table thead th {
    border-top: none;
    font-weight: 600;
    color: #5a5c69;
    background-color: #f8f9fc;
}

.table tbody tr:hover {
    background-color: #f8f9fc;
}

/* Badge de posição */
.position-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.375rem 0.75rem;
    border-radius: 20px;
    font-weight: 700;
    font-size: 0.875rem;
}

.position-badge.first {
    background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
    color: #b45309;
}

.position-badge.second {
    background: linear-gradient(135deg, #c0c0c0 0%, #d3d3d3 100%);
    color: #495057;
}

.position-badge.third {
    background: linear-gradient(135deg, #cd7f32 0%, #daa520 100%);
    color: #fff;
}

.position-badge.other {
    background: #e9ecef;
    color: #6c757d;
}

/* Informações do departamento na linha */
.department-info-row {
    display: flex;
    align-items: center;
}

.department-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: linear-gradient(135deg, #36b9cc 0%, #258391 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;
    font-size: 1.25rem;
}

.department-details .department-name {
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 0.25rem;
}

.department-meta {
    font-size: 0.75rem;
}

/* Barra de participação */
.participation-bar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.participation-progress {
    flex: 1;
    height: 8px;
    background: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: linear-gradient(135deg, #36b9cc 0%, #258391 100%);
    transition: width 0.3s ease;
}

.participation-text {
    font-size: 0.75rem;
    font-weight: 600;
    color: #495057;
    min-width: 40px;
}

/* Análise de distribuição */
.distribution-analysis {
    margin-top: 2rem;
}

.analysis-card {
    background: white;
    border-radius: 8px;
    padding: 1.5rem;
    border: 1px solid #e3e6f0;
    height: 100%;
}

.analysis-title {
    color: #2d3748;
    font-weight: 600;
    margin-bottom: 1rem;
    border-bottom: 1px solid #e3e6f0;
    padding-bottom: 0.5rem;
}

.analysis-content {
    color: #4a5568;
}

.analysis-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid #f1f3f4;
}

.analysis-item:last-child {
    border-bottom: none;
}

.analysis-label {
    font-weight: 500;
    color: #4a5568;
}

.analysis-value {
    font-weight: 700;
    color: #2d3748;
}

/* Badges */
.badge {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
}

/* Formulários */
.form-label {
    font-weight: 600;
    color: #5a5c69;
    margin-bottom: 0.5rem;
}

.form-control {
    border-radius: 0.35rem;
    border: 1px solid #d1d3e2;
}

.form-control:focus {
    border-color: #36b9cc;
    box-shadow: 0 0 0 0.2rem rgba(54, 185, 204, 0.25);
}

/* Responsividade */
@media (max-width: 768px) {
    .podium-card {
        height: 150px;
        margin-bottom: 1rem;
    }

    .podium-card.first-place {
        transform: none;
        order: 1;
    }

    .medal-icon {
        font-size: 1.5rem;
    }

    .department-name {
        font-size: 0.9rem;
    }

    .stat-value {
        font-size: 1.25rem;
    }

    .department-info-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
    }

    .department-icon {
        margin-right: 0;
    }

    .participation-bar {
        flex-direction: column;
        align-items: stretch;
    }

    .analysis-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.25rem;
    }

    .table-responsive {
        font-size: 0.875rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/Indicators/ranking_departments.vue